/**
 * Distributor billing service — wraps PR-3 + PR-5's distributor-billing
 * endpoints.
 *
 * Every call is tenant-resolved SERVER-SIDE via effective_distributor_id()
 * (impersonation rides on the axios interceptor app-wide). The frontend never
 * passes a distributor id — the reads + cancel take no arguments by design;
 * plan-change-request carries only the requested tier key.
 *
 *   GET  /api/distributor/billing/plan
 *   GET  /api/distributor/billing/stripe-connect
 *   GET  /api/distributor/billing/estimated-invoice
 *   GET  /api/distributor/billing/tiers                  (PR-5)
 *   POST /api/distributor/billing/cancel                 (PR-5)
 *   POST /api/distributor/billing/plan-change-request    (PR-5)
 *   POST /api/distributor/billing/payment-method        (billing card)
 *
 * Stripe Connect onboarding (route-fork, regression fix):
 *   GET  /api/stripe/connect/onboard          -> { url } (redirect target)
 *   POST /api/stripe/connect/refresh-status   -> live-flag re-sync on return
 */
import api from './api'

export function getBillingPlan() {
  return api.get('/distributor/billing/plan')
}

export function getStripeConnect() {
  return api.get('/distributor/billing/stripe-connect')
}

// Route-fork Stripe Connect onboarding init. Returns { data: { url } } -- the
// caller redirects the browser to that Stripe onboarding url. Tenant resolved
// server-side (impersonation-aware); no distributor id is sent.
export function startStripeConnect() {
  return api.get('/stripe/connect/onboard')
}

// Existing-account path (plan Unit 1). DISTINCT from startStripeConnect above:
// that one creates a NEW Stripe account via Account Links, this one attaches an
// account the distributor ALREADY has, via OAuth. They must never be collapsed
// -- an existing-account button that falls back to account creation mints a
// second empty account behind a green "Connected" badge.
//
// `intendedAccountId` is the acct_ the distributor says they mean to connect,
// and it is optional. It matters because Stripe does not refuse an account
// controlled by another platform: it creates a SIBLING account and
// auto-connects it, so the callback needs something to compare against or the
// swap is invisible. Returns { data: { url } }; the caller redirects.
export function startStripeConnectOAuth(intendedAccountId) {
  return api.get('/stripe/connect/oauth/authorize', {
    params: intendedAccountId ? { intended_account_id: intendedAccountId } : {},
  })
}

// Complete the OAuth handshake. The browser lands back on the Payments page
// with ?code=&state= from Stripe; this hands both to the backend, which
// validates the state, exchanges the code, and decides whether the returned
// account is the intended one. Returns a status of connected /
// different_account / unverified_account / declined.
export function completeStripeConnectOAuth(code, state) {
  return api.get('/stripe/connect/oauth/callback', { params: { code, state } })
}

// Explicitly accept an account Stripe returned that was NOT the intended one.
// Takes the SIGNED pending token minted by the callback, never a bare acct_ --
// a bare id from the client would let anyone bind an arbitrary account by
// POSTing to this route.
export function acceptDifferentStripeAccount(pendingToken) {
  return api.post('/stripe/connect/oauth/accept-different-account', {
    pending_token: pendingToken,
  })
}

// Disconnect (deauthorize) the tenant's Stripe Connect account.
//
// TAKES NO ARGUMENTS, and that is the fix for a live bug (2026-09-04). This
// used to take the platform's Connect client id and interpolate it into the
// path. Three components each held their own literal copy of that id, all
// three had gone stale, and Stripe rejected every disconnect -- while the
// backend answered 200 carrying the error, so the UI reported nothing and the
// account stayed connected.
//
// The client id identifies OUR PLATFORM. It is constant for a deployment, the
// browser has no business asserting it, and the backend already read it from
// STRIPE_CONNECT_CLIENT_ID for the connect leg. The deauthorize endpoint now
// does the same, so there is nowhere for a stale copy to live. Do not add a
// parameter back, not even as a default -- a stale fallback IS this bug.
//
// RESPONSE CONTRACT, and why callers must catch:
//   200 {"message": "success"}                     -- disconnected
//   404 {"message": "no connected Stripe account"}  -- nothing to disconnect
//   502 -- Stripe refused the revoke; still connected
//   503 -- STRIPE_CONNECT_CLIENT_ID unset on this environment
//   500 -- unexpected failure; still connected
//
// The 404 is deliberate and load-bearing. It used to be a 200 with a message
// body, which meant a caller could not tell "nothing to disconnect" from
// "disconnected successfully" -- exactly how an OAuth-connected tenant's
// disconnect silently did nothing while the UI reported success. The 502/503/
// 500 codes replace a second instance of that same anti-pattern: the backend
// used to answer 200 carrying the error object for BOTH failure arms.
//
// A non-2xx REJECTS (no validateStatus override, and the auth interceptor
// rejects everything that is not a 401), so every caller MUST wrap this in
// try/catch; an uncaught rejection leaves the user with no feedback at all.
export function disconnectStripeConnect() {
  return api.get('/stripe/connect/deauthorize')
}

// Refresh-on-return: after the browser comes back from Stripe onboarding, sync
// the live Connect flags onto the distributor row so the status card reflects
// real state. Tenant resolved server-side; body is empty.
export function refreshStripeConnectStatus() {
  return api.post('/stripe/connect/refresh-status', {})
}

export function getEstimatedInvoice() {
  return api.get('/distributor/billing/estimated-invoice')
}

// Per-store billing for one month ("YYYY-MM"; omit for the current month).
// Tenant resolved server-side; the month is the only parameter.
export function getStoreBilling(month) {
  return api.get('/distributor/billing/stores', {
    params: month ? { month } : {},
  })
}

// PR-5: tier comparison data for the plan-change modal (both self-serve and
// gated tiers; current_tier_key marks the caller's current plan).
export function getTiers() {
  return api.get('/distributor/billing/tiers')
}

// PR-5: sales-mediated "Talk to sales about Volume" request. Tenant resolved
// server-side; the body carries only the requested tier key.
export function requestPlanChange(tierKey) {
  return api.post('/distributor/billing/plan-change-request', {
    requested_tier_key: tierKey,
  })
}

// PR-5: cancel the distributor subscription (danger zone). Body is empty —
// the tenant is resolved server-side, never sent from the client.
export function cancelSubscription() {
  return api.post('/distributor/billing/cancel', {})
}

// ---------------------------------------------------------------------------
// ACH Direct Debit -- the ThreadFlow BILLING relationship (Unit 5)
//
// This is how WE invoice THE DISTRIBUTOR on the 1st and 15th. It is NOT
// Stripe Connect (that is how the distributor gets paid by shoppers, above)
// and the two must not be conflated: Connect calls carry a connected-account
// context, these are platform-side calls against ThreadFlow's own Customer.
//
// Tenant is resolved SERVER-SIDE on all three, so no distributor id is ever
// sent from the client.
// ---------------------------------------------------------------------------

// Current ACH status: "not_set_up" or "active", plus display-safe bank
// identity (name + last4 only -- we never hold a full account number).
export function getAchStatus() {
  return api.get('/distributor/billing/ach')
}

// Start bank-account setup. Returns a client_secret for a us_bank_account
// SetupIntent, which Stripe.js uses to collect and verify the account. Body
// is empty.
export function createAchSetupIntent() {
  return api.post('/distributor/billing/ach/setup-intent', {})
}

// Record the ACH mandate after Stripe confirms the bank account, and make it
// the default payment method for future invoices. The server re-fetches the
// SetupIntent from Stripe and refuses one that is not the caller's own, so
// this id is a reference, not a trusted assertion.
export function confirmAchMandate(setupIntentId) {
  return api.post('/distributor/billing/ach/confirm', {
    setup_intent_id: setupIntentId,
  })
}

// Replace the distributor's ThreadFlow billing CARD. Sends ONLY the new
// browser-tokenized PaymentMethod id: the server resolves the billing
// Customer from the caller's own subscription, so there is no customer id to
// send (or to spoof). Tokenize with the plan payload's
// `stripe_publishable_key` so the card exists on the distributor's billing
// platform. The server provisions the billing Customer when none exists yet, so a
// card can be the first billing method. 409 `status` is `ach_active` (bank
// billing is active; card updates are refused).
export function updateBillingCard(paymentMethodId) {
  return api.post('/distributor/billing/payment-method', {
    payment_method_id: paymentMethodId,
  })
}
